import { ReactNode, RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

type MenuPopoverProps = {
    open: boolean;
    onClose: () => void;
    anchorRef: RefObject<HTMLElement>;
    label: string;
    width?: number;
    children: ReactNode;
};

const GAP = 12;
const VIEWPORT_MARGIN = 16;

// Popover ancorado a direita de um item do menu lateral; alinha a base ao acionador.
export default function MenuPopover({ open, onClose, anchorRef, label, width = 352, children }: MenuPopoverProps) {
    const panelRef = useRef<HTMLDivElement>(null);
    const location = useLocation();
    const [position, setPosition] = useState({ left: 0, bottom: 0, maxHeight: 0 });

    useLayoutEffect(() => {
        if (!open) return;

        const update = () => {
            const rect = anchorRef.current?.getBoundingClientRect();
            if (!rect) return;
            const left = Math.min(rect.right + GAP, window.innerWidth - width - VIEWPORT_MARGIN);
            const bottom = Math.max(window.innerHeight - rect.bottom, VIEWPORT_MARGIN);
            setPosition({ left, bottom, maxHeight: window.innerHeight - bottom - VIEWPORT_MARGIN });
        };

        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, [open, anchorRef, width]);

    useEffect(() => {
        if (!open) return;

        const onPointerDown = (event: PointerEvent) => {
            const target = event.target as Node;
            if (panelRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
            onClose();
        };
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            onClose();
            anchorRef.current?.focus();
        };

        document.addEventListener('pointerdown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open, onClose, anchorRef]);

    useEffect(() => {
        if (open) onClose();
        // Fecha ao navegar; demais dependencias sao intencionais apenas na troca de rota.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, location.search]);

    if (!open || typeof document === 'undefined') return null;

    return createPortal(
        <div
            ref={panelRef}
            className="sf-popover fixed z-[10001] flex flex-col overflow-hidden"
            style={{ left: position.left, bottom: position.bottom, width, maxHeight: position.maxHeight }}
            role="dialog"
            aria-label={label}
        >
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">{children}</div>
        </div>,
        document.body,
    );
}
